import { supportedLanguages, SupportedLanguagesCodes } from '../constants';

/**
 * Formats a given number according to the specified locale.
 *
 * @param {number} value - The number to be formatted.
 * @param {SupportedLanguagesCodes} [locale='it'] - The locale to use for formatting. Defaults to 'it' (Italian).
 * @returns {string} - The formatted number as a string.
 *
 * @description
 * This function takes a numeric value and formats it based on the specified locale ('it' for Italian or 'en' for English).
 * It uses the `Intl.NumberFormat` API to apply the appropriate formatting style. The locale code is retrieved from the
 * `supportedLanguages` array, which is imported from the `../constants` module. If the locale is not found in the array,
 * the function may throw an error due to an undefined `localeCode`.
 *
 * @dependencies
 * - `supportedLanguages`: An array of objects, each containing a `value` (locale identifier) and a `langCode` (locale code).
 *   This array must include entries for 'it' and 'en' to ensure proper functionality.
 *
 * @example
 * // Example usage:
 * import { formatNumber } from './utils';
 *
 * const formattedNumber1 = formatNumber(1234567.89, 'it'); // "1.234.567,89"
 * const formattedNumber2 = formatNumber(1234567.89, 'en'); // "1,234,567.89"
 *
 * console.log(formattedNumber1);
 * console.log(formattedNumber2);
 */
export function formatNumber(value: number, locale: SupportedLanguagesCodes = 'it'): string {
  const localeCode = supportedLanguages.find(lang => lang.value === locale)?.langCode;
  return new Intl.NumberFormat(localeCode, { style: 'decimal' }).format(value);
}
